import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { SubjectLanguage } from '../types';

import type { RequirementRowView } from './PatientRequirementsSectionView';

/** Mirrors the contract: `WaiveRequest.reason` is "non-blank, capped". */
const REASON_MAX = 280;

export interface WaiveRequirementDialogProps {
  /** The row being waived, or null when closed. */
  row: RequirementRowView | null;
  subject: SubjectLanguage;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Waiving asks for a reason because the contract requires one (AC-8), and
 * because a waived row that does not say why leaves the next staff member
 * unable to tell a decision from a mistake.
 *
 * The reason is free text a person typed, so it never reaches an analytics
 * payload — only the fact that a waive happened does.
 */
export const WaiveRequirementDialog = ({
  row,
  subject,
  onClose,
  onConfirm,
}: WaiveRequirementDialogProps) => {
  const [reason, setReason] = useState('');

  // Reset per row, not per close: reopening the dialog for a DIFFERENT row must
  // not inherit the previous row's half-typed reason.
  useEffect(() => {
    setReason('');
  }, [row?.patientRequirementId]);

  const trimmed = reason.trim();
  const tooLong = trimmed.length > REASON_MAX;
  const canConfirm = trimmed.length > 0 && !tooLong;

  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Waive {row?.label ?? 'this requirement'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            This {subject.singular} will stop appearing on the missing list for this
            item. You can reopen it later.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Reason"
            value={reason}
            onChange={event => setReason(event.target.value)}
            error={tooLong}
            helperText={
              tooLong
                ? `Keep it under ${REASON_MAX} characters.`
                : 'Recorded on the requirement so staff can see why.'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {/* Withheld rather than disabled-with-a-handler: an empty reason is a
            400 from the contract, so the action must not be callable at all. */}
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={canConfirm ? () => onConfirm(trimmed) : undefined}
          sx={{ textTransform: 'none' }}
        >
          Waive
        </Button>
      </DialogActions>
    </Dialog>
  );
};
